import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService } from '../catalog/catalog.service';
import {
  CreateSalesReservationPayload,
  ReservationHoldRecord,
  ReservationReleaseRecord,
  SalesApiService,
  SalesChannelRecord
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-reservation-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TableModule, DialogModule, TextareaModule, CurrencySelectorComponent,
    CustomerSelectorComponent, JalaliDateInputComponent, AinDatePipe
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.reservations.form.createTitle') : t('sales.reservations.form.editTitle')" [description]="t('sales.reservations.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <header class="card header">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/sales/reservations']">{{ t('catalog.action.backToList') }}</a>
            <div class="actions">
              @if (!isCreateMode() && status() === 'Active') {
                <button pButton type="button" severity="danger" [label]="t('sales.reservations.action.release')" [disabled]="saving()" (click)="showReleaseDialog.set(true)"></button>
              }
              @if (isCreateMode()) {
                <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
              }
            </div>
          </div>
          @if (!isCreateMode()) {
            <div class="summary">
              <span>{{ t('sales.reservations.fields.reservationKey') }}: <strong>{{ form.reservationKey }}</strong></span>
              <span>{{ t('columns.status') }}: <strong>{{ statusLabel(status()) }}</strong></span>
              <span>{{ t('sales.reservations.fields.reservationType') }}: <strong>{{ typeLabel(form.reservationType) }}</strong></span>
              <span>{{ t('sales.reservations.fields.quantity') }}: <strong>{{ form.reservedQuantity }}</strong></span>
              <span>{{ t('sales.reservations.fields.expiresAt') }}: <strong>{{ form.expiresAtUtc | ainDate:'datetime' }}</strong></span>
            </div>
          }
        </header>

        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.reservations.fields.reservationKey') }}</span><input pInputText [(ngModel)]="form.reservationKey" /></label>
            <label>
              <span>{{ t('sales.reservations.fields.reservationType') }}</span>
              <p-select [options]="typeOptions" optionValue="value" [(ngModel)]="form.reservationType" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('sales.orders.fields.channel') }}</span>
              <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.salesChannelId" [showClear]="true" appendTo="body" />
            </label>
            <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
            <label>
              <span>{{ t('sales.reservations.fields.offering') }}</span>
              <p-select [options]="offeringOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.offeringId" [showClear]="true" [filter]="true" appendTo="body" />
            </label>
            <label><span>{{ t('sales.reservations.fields.quantity') }}</span><p-inputNumber [(ngModel)]="form.reservedQuantity" [min]="0" /></label>
            <label><span>{{ t('sales.reservations.fields.amount') }}</span><p-inputNumber [(ngModel)]="form.reservedAmount" [min]="0" /></label>
            <label><span>{{ t('sales.orders.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
            <label><span>{{ t('sales.reservations.fields.expiresAt') }}</span><ain-date-input [(ngModel)]="form.expiresAtUtc" /></label>
            <label><span>{{ t('sales.reservations.fields.idempotencyKey') }}</span><input pInputText [(ngModel)]="form.idempotencyKey" /></label>
            <label class="full"><span>{{ t('sales.reservations.fields.notes') }}</span><textarea pTextarea rows="3" [(ngModel)]="form.notes"></textarea></label>
          </section>
        } @else {
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('sales.reservations.tabs.general') }}</button>
            <button type="button" [class.active]="activeTab() === 'holds'" (click)="activeTab.set('holds')">{{ t('sales.reservations.tabs.holds') }}</button>
            <button type="button" [class.active]="activeTab() === 'releases'" (click)="activeTab.set('releases')">{{ t('sales.reservations.tabs.releases') }}</button>
          </div>

          @if (activeTab() === 'general') {
            <section class="card grid">
              <label><span>{{ t('sales.orders.fields.channel') }}</span><input pInputText [ngModel]="channelLabel(form.salesChannelId)" readonly /></label>
              <label><span>{{ t('sales.orders.fields.customer') }}</span><input pInputText [ngModel]="form.customerAccountId || '-'" readonly /></label>
              <label><span>{{ t('sales.reservations.fields.offering') }}</span><input pInputText [ngModel]="offeringLabel(form.offeringId)" readonly /></label>
              <label><span>{{ t('sales.reservations.fields.amount') }}</span><input pInputText [ngModel]="form.reservedAmount ?? '-'" readonly /></label>
              <label><span>{{ t('sales.orders.fields.currency') }}</span><input pInputText [ngModel]="form.currencyCode || '-'" readonly /></label>
              <label><span>{{ t('sales.reservations.fields.heldAt') }}</span><input pInputText [ngModel]="form.heldAtUtc | ainDate:'datetime'" readonly /></label>
              <label class="full"><span>{{ t('sales.reservations.fields.notes') }}</span><textarea pTextarea rows="3" [(ngModel)]="form.notes" readonly></textarea></label>
            </section>
          }

          @if (activeTab() === 'holds') {
            <section class="card">
              <p-table [value]="holds()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('sales.reservations.fields.quantity') }}</th>
                    <th>{{ t('sales.reservations.fields.amount') }}</th>
                    <th>{{ t('sales.reservations.fields.heldAt') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.heldQuantity }}</td>
                    <td>{{ row.heldAmount ?? '-' }}</td>
                    <td>{{ row.heldAtUtc | ainDate:'datetime' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          }

          @if (activeTab() === 'releases') {
            <section class="card">
              <p-table [value]="releases()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('sales.reservations.fields.releasedAt') }}</th>
                    <th>{{ t('sales.reservations.fields.releaseReason') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.releasedAtUtc | ainDate:'datetime' }}</td>
                    <td>{{ row.reason || '-' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          }
        }

        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>

    <p-dialog [header]="t('sales.reservations.action.release')" [visible]="showReleaseDialog()" (visibleChange)="showReleaseDialog.set($event)" [modal]="true" [style]="{ width: '420px' }">
      <label class="dialog-field">
        <span>{{ t('sales.reservations.fields.releaseReason') }}</span>
        <textarea pTextarea rows="3" [(ngModel)]="releaseReason"></textarea>
      </label>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="showReleaseDialog.set(false)"></button>
        <button pButton type="button" severity="danger" [label]="t('sales.reservations.action.release')" [disabled]="saving()" (click)="release()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .actions { display: flex; gap: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; margin-bottom: 10px; }
    .header .summary { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 10px; font-size: 13px; color: #475569; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .grid .full { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .dialog-field { display: grid; gap: 6px; width: 100%; }
    .tabs { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .tabs button { border: 1px solid #cbd5e1; background: #fff; border-radius: 999px; padding: 6px 12px; cursor: pointer; }
    .tabs button.active { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesReservationDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly catalog = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly status = signal('Active');
  readonly activeTab = signal<'general' | 'holds' | 'releases'>('general');
  readonly holds = signal<ReservationHoldRecord[]>([]);
  readonly releases = signal<ReservationReleaseRecord[]>([]);
  readonly channels = signal<SalesChannelRecord[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly showReleaseDialog = signal(false);

  releaseReason = '';

  readonly typeOptions = [
    { value: 'Stock', labelKey: 'sales.reservations.types.stock' },
    { value: 'Capacity', labelKey: 'sales.reservations.types.capacity' },
    { value: 'TimeWindow', labelKey: 'sales.reservations.types.timeWindow' }
  ];

  form = {
    reservationKey: '',
    reservationType: 'Stock',
    salesChannelId: '' as string | null,
    customerAccountId: '' as string | null,
    offeringId: '' as string | null,
    reservedQuantity: 1,
    reservedAmount: null as number | null,
    currencyCode: 'IRR',
    heldAtUtc: '' as string | null,
    expiresAtUtc: '' as string | null,
    idempotencyKey: '',
    notes: ''
  };

  ngOnInit(): void {
    this.api.listChannels().subscribe({
      next: rows => this.channels.set(rows),
      error: () => this.channels.set([])
    });
    this.catalog.listOfferings().subscribe({
      next: rows => this.offerings.set(rows),
      error: () => this.offerings.set([])
    });

    const reservationId = this.route.snapshot.paramMap.get('reservationId');
    this.isCreateMode.set(!reservationId || reservationId === 'new');

    if (this.isCreateMode()) {
      this.form.expiresAtUtc = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      this.form.idempotencyKey = `res-${Date.now()}`;
      this.loading.set(false);
      return;
    }

    this.loadReservation(reservationId!);
  }

  channelOptions(): { label: string; value: string }[] {
    return this.channels().map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  offeringOptions(): { label: string; value: string }[] {
    return this.offerings().map(item => ({ label: `${item.code} - ${item.title}`, value: item.id }));
  }

  channelLabel(id: string | null | undefined): string {
    if (!id) return '-';
    return this.channelOptions().find(item => item.value === id)?.label ?? id;
  }

  offeringLabel(id: string | null | undefined): string {
    if (!id) return '-';
    return this.offeringOptions().find(item => item.value === id)?.label ?? id;
  }

  loadReservation(id: string): void {
    this.api.getReservation(id).subscribe({
      next: reservation => {
        this.status.set(reservation.status);
        this.form = {
          reservationKey: reservation.reservationKey,
          reservationType: reservation.reservationType,
          salesChannelId: reservation.salesChannelId ?? null,
          customerAccountId: reservation.customerAccountId ?? null,
          offeringId: reservation.offeringId ?? null,
          reservedQuantity: reservation.reservedQuantity,
          reservedAmount: reservation.reservedAmount ?? null,
          currencyCode: reservation.currencyCode ?? 'IRR',
          heldAtUtc: reservation.heldAtUtc,
          expiresAtUtc: reservation.expiresAtUtc,
          idempotencyKey: reservation.idempotencyKey,
          notes: reservation.notes ?? ''
        };
        this.loadHolds(id);
        this.loadReleases(id);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('sales.reservations.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  loadHolds(id: string): void {
    this.api.listReservationHolds(id).subscribe({
      next: rows => this.holds.set(rows),
      error: () => this.holds.set([])
    });
  }

  loadReleases(id: string): void {
    this.api.listReservationReleases(id).subscribe({
      next: rows => this.releases.set(rows),
      error: () => this.releases.set([])
    });
  }

  save(): void {
    if (!this.form.expiresAtUtc) {
      this.errorMessage.set(this.t('sales.reservations.errorExpiryRequired'));
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');
    const payload: CreateSalesReservationPayload = {
      reservationKey: this.form.reservationKey,
      reservationType: this.form.reservationType,
      salesChannelId: this.form.salesChannelId || null,
      customerAccountId: this.form.customerAccountId || null,
      offeringId: this.form.offeringId || null,
      reservedQuantity: this.form.reservedQuantity,
      reservedAmount: this.form.reservedAmount,
      currencyCode: this.form.reservedAmount ? this.form.currencyCode : null,
      heldAtUtc: null,
      expiresAtUtc: this.form.expiresAtUtc,
      idempotencyKey: this.form.idempotencyKey,
      notes: this.form.notes || null
    };

    this.api.createReservation(payload).subscribe({
      next: reservation => void this.router.navigate(['/sales/reservations', reservation.id]),
      error: () => {
        this.errorMessage.set(this.t('sales.reservations.errorSave'));
        this.saving.set(false);
      }
    });
  }

  release(): void {
    const id = this.route.snapshot.paramMap.get('reservationId')!;
    this.saving.set(true);
    this.api.releaseReservation(id, {
      reason: this.releaseReason || null,
      idempotencyKey: `release-${id}`
    }).subscribe({
      next: reservation => {
        this.status.set(reservation.status);
        this.showReleaseDialog.set(false);
        this.loadReleases(id);
        this.saving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('sales.reservations.errorRelease'));
        this.saving.set(false);
      }
    });
  }

  typeLabel(type: string): string {
    return this.localization.translate(`sales.reservations.types.${type.charAt(0).toLowerCase()}${type.slice(1)}`);
  }

  statusLabel(status: string): string {
    return this.localization.translate(`sales.reservations.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
